import { Link } from "react-router-dom";
import { ShieldCheck, Users, Gauge } from "lucide-react";
import { BloqLogo } from "@/components/brand/BloqLogo";
import { WaitlistForm } from "@/components/lander/WaitlistForm";

const TILES = [
  { icon: ShieldCheck, title: "BLOQ Shield", body: "Scams, spam & fake support blocked before they reach you.", c: "text-bloq-mint", glow: "rgba(1,250,189,0.16)" },
  { icon: Users, title: "Trust Gates", body: "BLOQs that require a real BLOQ Score to enter.", c: "text-bloq-blue", glow: "rgba(53,145,244,0.16)" },
  { icon: Gauge, title: "BLOQ Score", body: "Your portable trust passport across the Web3 world.", c: "text-bloq-purple", glow: "rgba(139,92,246,0.16)" },
];

// Variant 3 — "Trust Grid": storytelling teaser.
export default function TrustGrid() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-bloq-bg text-white">
      <div className="pointer-events-none absolute inset-0 grain opacity-50" />
      <div className="pointer-events-none absolute -top-40 -left-24 h-[480px] w-[480px] rounded-full bg-bloq-mint/10 blur-[150px] animate-float" />
      <div className="pointer-events-none absolute top-1/2 -right-24 h-[440px] w-[440px] rounded-full bg-bloq-purple/12 blur-[150px]" />

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-5 py-6 sm:px-8">
        <BloqLogo size={26} />
        <span className="rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-bloq-mint" data-testid="lander-beta-pill">
          Currently in private beta testing
        </span>
      </header>

      <main className="relative z-10 mx-auto grid max-w-6xl items-center gap-12 px-5 pb-20 pt-8 sm:px-8 lg:grid-cols-2 lg:gap-16 lg:pt-16">
        <div>
          <h1 className="animate-fade-up text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            The trust-gated
            <br /> messenger for
            <br /> <span className="bloq-gradient-text">the Web3 world.</span>
          </h1>
          <p className="mt-6 max-w-md animate-fade-up text-base text-bloq-muted" style={{ animationDelay: "120ms" }}>
            We're putting the finishing touches on something that makes Web3 conversations safe by default. Get on the list for early access.
          </p>
          <div className="mt-8 max-w-md animate-fade-up" style={{ animationDelay: "200ms" }}>
            <WaitlistForm founding />
          </div>
          <div className="mt-6 flex items-center gap-5 text-xs text-bloq-muted">
            <Link to="/privacy" className="hover:text-white" data-testid="lander-privacy-link">Privacy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="lander-terms-link">Terms</Link>
            <Link to="/data-security" className="hover:text-white" data-testid="lander-security-link">Data Security</Link>
            <Link to="/delete-account" className="hover:text-white" data-testid="lander-delete-link">Delete Account</Link>
          </div>
        </div>

        <div className="grid gap-4">
          {TILES.map((t, i) => (
            <div
              key={t.title}
              className="group relative animate-fade-up overflow-hidden rounded-3xl border border-white/8 bg-bloq-s1/70 p-6 backdrop-blur-xl transition-transform hover:-translate-y-1"
              style={{ animationDelay: `${180 + i * 90}ms` }}
            >
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity group-hover:opacity-100" style={{ background: t.glow }} />
              <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-bloq-s2">
                <t.icon className={`h-5 w-5 ${t.c}`} />
              </span>
              <h3 className="text-lg font-semibold">{t.title}</h3>
              <p className="mt-1 text-sm text-bloq-muted">{t.body}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
